/**
 * The caller's shift write scope (#2043): `GET /api/v1/shifts/write-scope`. An admin issues
 * no request and is unrestricted. Until the answer arrives the planner offers every action:
 * most non-admin planners lead no group, and the backend refuses an out-of-scope write with
 * 403 SHIFT_WRITE_OUT_OF_SCOPE whatever the planner shows.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { ShiftsService } from '@/api/services/ShiftsService';
import { useAuthContext } from '@/contexts/AuthContext';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import {
  UNRESTRICTED_WRITE_SCOPE,
  type ShiftWriteScope,
} from '@/components/shifts/shiftWriteScope';

export const shiftWriteScopeKeys = {
  me: (orgId: string, userId: string) => ['shift-write-scope', orgId, userId] as const,
};

export function useShiftWriteScope(): ShiftWriteScope & { isLoading: boolean } {
  const isAdmin = useIsAdmin();
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId, userId } = useAuthContext();

  const query = useQuery({
    queryKey: shiftWriteScopeKeys.me(orgId ?? '', userId ?? ''),
    queryFn: () => ShiftsService.getApiV1ShiftsWriteScope(),
    enabled: !isAdmin && !!isSignedIn && isAuthReady && !!orgId,
    staleTime: 60_000,
    retry: false,
  });

  const { data, isLoading } = query;
  // One object per answer, so consumers can depend on it.
  return useMemo(() => {
    if (isAdmin) return { ...UNRESTRICTED_WRITE_SCOPE, isLoading: false };
    if (!data) return { ...UNRESTRICTED_WRITE_SCOPE, isLoading };
    return {
      unrestricted: data.unrestricted,
      groupIds: data.group_ids ?? [],
      allEmployees: data.all_employees,
      employeeIds: data.employee_ids ?? [],
      isLoading: false,
    };
  }, [isAdmin, data, isLoading]);
}
